import { Alert, Badge, Chip, InfoTip } from '@zarya/ui';
import { t } from '@app/i18n';
import { withEmphasis } from '@app/i18n/emphasis';
import { dataHour } from '@app/format';
import { WhyDenied, causaOf, causaVocab, type Diagnostic } from '@app/main/Decisions';
import type { SimulationDatada } from './Simulator.api';
import { readDiagnostic } from './diagnostic';
import s from './Simulator.module.css';

/**
 * Card **Resultado** de T18 (FR-100, FR-101).
 *
 * Três regras que não são cosméticas:
 *
 * 1. **`simulado: false` não tem veredito.** "Não foi possível simular: {motivo}"
 *    ocupa o lugar do bloco ALLOW/DENY. Um veredito com asterisco de "não deu
 *    para avaliar" seria pior que a ausência dele.
 * 2. **ALLOW só é limpo quando o diagnóstico não reporta verificação faltando.**
 *    Se reporta, o rótulo é "ALLOW ignorando os limites" e o aviso vem **junto**
 *    do veredito, não numa seção adiante. E o `diagnostico` cru aparece sempre:
 *    a detecção é heurística sobre objeto de forma livre, e o objeto é a garantia
 *    de que um qualificador que ela não reconheceu ainda chegue aos olhos.
 * 3. **O resultado é datado.** O documento do PDP muda a cada publicação de
 *    contrato ou política; dois operadores — um simulando, outro publicando —
 *    produzem um veredito já obsoleto na tela. O instante da avaliação é o que
 *    transforma o veredito em fato verificável.
 */

export interface ResultBlockProps {
  simulacao: SimulationDatada;
}

/**
 * A linha «simulado como» — só no modo Claims (JSON), onde `subject_claims` vem
 * preenchido (04/09).
 *
 * Por que ela é obrigatória, e não um detalhe: no modo Claims o sujeito foi
 * MONTADO, e um veredito sem dizer sobre quem é não é informação — o operador
 * troca `perfil` três vezes em trinta segundos e precisa saber a qual resposta
 * está olhando. Fica junto do veredito por isso.
 *
 * E ela diz também que o sujeito foi TOMADO COMO DADO. Não é ressalva de rodapé:
 * o veredito acima vale para «alguém com estas claims», e não prova que exista
 * alguém assim — numa chamada de verdade quem prova isso é a assinatura do IdP.
 * Sem a frase, um ALLOW simulado se leria como um ALLOW real.
 *
 * (Até 04/09 a frase dizia outra coisa: que no lab o sujeito montado seria
 * recusado. Deixou de ser verdade quando o Rego ganhou o modo simulação —
 * `input.simulation`, `barriers.token_valid: "simulated"`. Uma ressalva que
 * descreve um comportamento que mudou é pior que nenhuma: ela ensina errado.)
 */
function SimulatedAs({ claims }: { claims: Record<string, unknown> }) {
  const pares = Object.entries(claims);
  if (pares.length === 0) return null;

  return (
    <p className={s.simuladoComo} data-testid="simulado-como">
      <span className={s.simuladoComoRotulo}>{t('simulator.simulatedAs.label')}</span>
      {pares.map(([claim, valor]) => (
        <Chip key={claim} className={s.claimChip}>{`${claim}=${valorDaClaim(valor)}`}</Chip>
      ))}
      <InfoTip
        text={t('simulator.simulatedAs.tip')}
        label={t('simulator.simulatedAs.tipLabel')}
        size="xs"
      />
    </p>
  );
}

/** O valor como o operador o escreveu: lista vira `a, b`, o resto é ele mesmo. */
function valorDaClaim(valor: unknown): string {
  if (Array.isArray(valor)) return valor.join(', ');
  if (valor === null) return 'null';
  if (typeof valor === 'object') return JSON.stringify(valor);
  return String(valor);
}

export function ResultBlock({ simulacao }: ResultBlockProps) {
  const { resultado, avaliadoEm, target } = simulacao;
  const diagnostico = readDiagnostic(resultado.diagnostic as Record<string, unknown> | null);
  // `subject_claims` vem só no modo Claims; `null` no modo JWT (o contrato não
  // decodifica o token colado de propósito).
  const subjectClaims = (resultado.subject_claims ?? null) as Record<string, unknown> | null;

  // FR-101: não avaliável não recebe veredito nenhum.
  if (!resultado.simulated) {
    return (
      <>
        <Alert tone="danger" title={t('simulator.notSimulated.title')}>
          {resultado.detail ?? t('simulator.notSimulated.noDetail')}
        </Alert>
        {/* Mesmo sem veredito: saber COM QUE pessoa se perguntou é o que evita
            repetir a mesma pergunta achando que se mudou algo. */}
        {subjectClaims ? <SimulatedAs claims={subjectClaims} /> : null}
        <p className={s.nota}>
          {/* `?? ''`: `dataHour` devolve `null` para data inválida, e o `t()` deixa
              o `{when}` CRU quando o parâmetro é `undefined` — a tela mostraria a
              chave. Antes da i18n o `null` ia direto ao JSX e sumia; `''` mantém
              exatamente esse comportamento. */}
          {t('simulator.notSimulated.note', {
            when: dataHour(avaliadoEm.toISOString()) ?? '',
          })}
        </p>
      </>
    );
  }

  const permitido = resultado.allowed;
  const inconclusivo = permitido === true && diagnostico.verificacaoFaltando;

  // O diagnóstico por eixo (P6.2), quando o executor o publica. É um objeto de
  // forma livre no contrato do `simulate`, então a checagem é por conteúdo e não
  // por tipo: só há bloco por eixo se houver eixo. Sem eles, o `detail` de
  // sempre — que é o que um executor mais antigo devolve.
  const cru = resultado.diagnostic as Record<string, unknown> | null | undefined;
  const porEixo =
    cru && ('person_axis' in cru || 'workload_axis' in cru) ? (cru as Diagnostic) : null;

  // O MESMO chip de T16, do mesmo catálogo. O simulador e a decisão real dizem a
  // mesma coisa com as mesmas palavras — se divergissem, o operador aprenderia
  // um vocabulário simulando e outro auditando.
  //
  // Só quando o executor a publica: `cause` é aditiva, e um executor anterior a
  // ela não a manda. Aí o bloco desenha os eixos sem chipar causa nenhuma, em vez
  // de chipar «sem diagnóstico» sobre um diagnóstico que existe.
  const causaDoSimulate = resultado.cause ? causaOf(resultado.cause) : null;

  const rotulo =
    permitido === true
      ? inconclusivo
        ? t('simulator.verdict.allowIgnoringLimits')
        : t('simulator.verdict.allow')
      : permitido === false
        ? t('simulator.verdict.deny')
        : t('simulator.verdict.undetermined');

  const classe =
    permitido === true
      ? inconclusivo
        ? s.vereditoRessalva
        : s.vereditoOk
      : permitido === false
        ? s.vereditoDgr
        : s.vereditoNeutro;

  return (
    <>
      <div className={s.vereditoBloco}>
        <strong className={[s.veredito, classe].join(' ')} data-testid="veredito">
          {rotulo}
        </strong>
        {/* `reason` é frase pronta do executor («workload sem concessão», no mock) — texto do
            servidor, em pt-BR, que ficava cru no console em `en` (marco 4). Com `cause` o
            badge ao lado e o «Por que negou» abaixo explicam no idioma da tela; sem `cause`
            (executor anterior) o `reason` aparece como dado, marcado como tal. */}
        {causaDoSimulate ? null : resultado.reason ? (
          <code className={s.motivo} data-origem="executor">
            {resultado.reason}
          </code>
        ) : (
          <span className={s.motivo}>{t('simulator.verdict.noReason')}</span>
        )}
        {causaDoSimulate ? (
          <Badge tone={causaVocab(causaDoSimulate).tone} tip={causaVocab(causaDoSimulate).tip}>
            {causaVocab(causaDoSimulate).rotulo}
          </Badge>
        ) : null}
        {resultado.with_token === false ? (
          <Badge tone="warning" tip={t('simulator.verdict.noToken.tip')}>
            {t('simulator.verdict.noToken.badge')}
          </Badge>
        ) : null}
      </div>

      {subjectClaims ? <SimulatedAs claims={subjectClaims} /> : null}

      {/*
        O DENY explicado, junto do veredito (P6.2 › diagnóstico por eixo).

        `detail` já vinha na resposta e só era exibido quando NÃO havia simulação
        — ou seja, nunca num DENY. Com a decisão em dois eixos ele passou a
        carregar a frase que ensina o modelo: qual eixo barrou e o que satisfez o
        outro («eixo pessoa: nenhuma política com condição de claim casa com quem
        pede · eixo workload satisfeito pelo contrato aprovado do agente»).

        Sem isto, essa frase só existia dentro do JSON cru do diagnóstico, que
        ninguém lê para entender um 403 — e a promessa da decisão era o motor
        ENSINAR o modelo a cada negação.
      */}
      {permitido === false && porEixo ? (
        // O MESMO bloco de T17 (`main/Decisions/WhyDenied`), não uma segunda
        // versão dele: a explicação de uma negação simulada e a de uma negação
        // real são a mesma frase sobre a mesma decisão, e duas cópias
        // divergiriam na primeira revisão de vocabulário.
        //
        // Enquanto o `simulate` do mock não publica `causa` (ela chega com o
        // Rego novo), o bloco desenha os eixos sem chipar causa nenhuma — que é
        // exatamente o que se sabe agora. Nada aqui deduz a causa do veredito.
        <WhyDenied
          causa={causaDoSimulate}
          diagnostico={porEixo}
          // A frase do `simulate` é campo de topo (`detail`), não do diagnóstico — é ela que diz
          // «eixo pessoa: … · eixo workload satisfeito …», e some da tela se não vier por aqui.
          frase={resultado.detail}
          // No modo A2A o alvo é o agente, e é ele que as frases de «O que faria
          // passar» têm de nomear: sem isto elas mandariam acrescentar a
          // delegação a Identity Access › Tools, onde ela não mora.
          contexto={
            target.kind === 'agent'
              ? { agent: target.agent }
              : { tool: typeof cru?.tool === 'string' ? cru.tool : target.tool || null }
          }
        />
      ) : permitido === false && resultado.detail ? (
        <Alert tone="info" title={t('simulator.whyDenied.title')}>
          {resultado.detail}
        </Alert>
      ) : null}

      {/* O aviso vive junto do veredito, não numa seção adiante. */}
      {inconclusivo ? (
        <Alert tone="warning" title={t('simulator.inconclusive.title')}>
          {withEmphasis(t('simulator.inconclusive.body'))}
          <ul className={s.trechos}>
            {diagnostico.trechos.map((trecho) => (
              <li key={trecho}>{trecho}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <dl className={s.defs}>
        <dt>
          {t('simulator.evaluatedAt.term')}
          <InfoTip
            text={t('simulator.evaluatedAt.tip')}
            label={t('simulator.evaluatedAt.tipLabel')}
            size="xs"
          />
        </dt>
        <dd>{dataHour(avaliadoEm.toISOString())}</dd>
        {/* A linha «Pod avaliado» saiu (aim-ivi.2, decisão 2 do mrolim, 11/10/2026): desde a D4 a simulação descreve a
            identidade pelo SPIFFE ID e não lê pod, e o campo saiu da resposta. */}
      </dl>

      {resultado.allowed_tools?.length ? (
        <section className={s.secao}>
          <h4 className={s.secaoTitulo}>{t('simulator.allowedTools.title')}</h4>
          <div className={s.chips}>
            {resultado.allowed_tools.map((tool) => (
              <Chip key={tool}>{tool}</Chip>
            ))}
          </div>
        </section>
      ) : null}

      {diagnostico.temConteudo ? (
        <section className={s.secao}>
          <h4 className={s.secaoTitulo}>
            {t('simulator.diagnostic.title')}
            <InfoTip
              text={t('simulator.diagnostic.tip')}
              label={t('simulator.diagnostic.tipLabel')}
              size="xs"
            />
          </h4>
          <pre className={s.json}>{JSON.stringify(resultado.diagnostic, null, 2)}</pre>
        </section>
      ) : null}

      {/* 04/09: a simulação passou a ser gravada com `simulated: true` e T17 a
          esconde por padrão. O texto antigo («não fica registrada, não aparece em
          Decisões nem na auditoria») contradizia o cabeçalho da própria tela. */}
      <p className={s.nota}>{withEmphasis(t('simulator.note'))}</p>
    </>
  );
}
